/**
 * The "Ran on <model>" sentence for a finished skill run's result window
 * (FRONT-2260). Pure so the exact wording can be pinned without rendering
 * anything — `skill-result-dialog.tsx` decides WHEN to show it (a run
 * that's no longer running and has a model); this decides WHAT it says.
 */
export interface SkillRunModelInfo {
  model?: string;
  modelFallbackFrom?: string;
  /** FRONT-2260 fix round 1 (F5): true when `model` is the workspace
   *  default. Absent (older runs) is treated the same as false — the
   *  " (workspace default)" suffix only appears when this is explicitly
   *  true, never as a guess from the model name. */
  modelIsDefault?: boolean;
  /** FRONT-2519: true when `model` is the model of the chat the run was
   *  started from. Takes the suffix over `modelIsDefault` (a chat on the
   *  workspace's own default model still reads as the chat's choice). Absent
   *  on every run that did not pick the chat's model. */
  modelIsChat?: boolean;
}

/**
 * `nameOf` resolves a model alias to its display name (e.g. via
 * `modelHeadline` over the `/api/llm/models` list), falling back to the raw
 * alias for one the caller couldn't resolve — this function never needs to
 * know why a name didn't resolve, only render whatever it's handed.
 */
export function skillRunModelLine(
  run: SkillRunModelInfo,
  nameOf: (alias: string) => string,
): string | null {
  if (!run.model) return null;
  let suffix = '';
  if (run.modelIsChat) {
    suffix = ' (chat model)';
  } else if (run.modelIsDefault) {
    suffix = ' (workspace default)';
  }
  const ranOn = `${nameOf(run.model)}${suffix}`;
  if (run.modelFallbackFrom) {
    return `Ran on ${ranOn} — ${nameOf(run.modelFallbackFrom)} wasn't available`;
  }
  return `Ran on ${ranOn}`;
}
